import StoreModuleService from '../../../StoreModuleService';
import { getWidget } from '../../../widgets/getters';
import { selectedWorkflowId } from '../../getters';
import {
	getConfigurationFieldsIds,
	getConfigurationFieldsIdsPerInterface,
	getSingleConfigurationInterfaceId,
} from '../../../workflowConfigurations/getters';
import {
	userSpecificConfigurationId as wfUserSpecificConfigurationId,
	publishedUserSpecificConfigurationId as wfPublishedUserSpecificConfigurationId,
	userSpecificConfiguration as wfUserSpecificConfiguration,
	generalConfigurationId as wfGeneralConfigurationId,
	publishedGeneralConfigurationId as wfPublishedGeneralConfigurationId,
	generalConfiguration as wfGeneralConfiguration,
	getConfigurationFieldResponse as wfGetConfigurationFieldResponse,
	userSpecificConfigurationResponse as wfUserSpecificConfigurationResponse,
	generalConfigurationResponse as wfGeneralConfigurationResponse,
	configurationsResponse as wfConfigurationsResponse,
} from '@/store/modules/workflows/getters';
import { MODULE_NAME } from './const';
import { FieldType } from '@/components/BlockEditorWidget/widgetFieldTypes.const';

const buildSelectedWorkflowConfigurationsGetterObject = (getter) => StoreModuleService.buildGetterObject(MODULE_NAME, getter);

// getters declarations
export const generalConfigurationId = buildSelectedWorkflowConfigurationsGetterObject('generalConfigurationId');
export const publishedGeneralConfigurationId = buildSelectedWorkflowConfigurationsGetterObject('publishedGeneralConfigurationId');
export const generalConfiguration = buildSelectedWorkflowConfigurationsGetterObject('generalConfiguration');
export const userSpecificConfigurationId = buildSelectedWorkflowConfigurationsGetterObject('userSpecificConfigurationId');
export const publishedUserSpecificConfigurationId = buildSelectedWorkflowConfigurationsGetterObject('publishedUserSpecificConfigurationId');
export const userSpecificConfiguration = buildSelectedWorkflowConfigurationsGetterObject('userSpecificConfiguration');
export const generalConfigurationResponse = buildSelectedWorkflowConfigurationsGetterObject('generalConfigurationResponse');
export const userSpecificConfigurationResponse = buildSelectedWorkflowConfigurationsGetterObject('userSpecificConfigurationResponse');
export const configurationsResponse = buildSelectedWorkflowConfigurationsGetterObject('configurationsResponse');
export const getConfigurationFieldResponse = buildSelectedWorkflowConfigurationsGetterObject('getConfigurationFieldResponse');

export const userSpecificConfigurationFieldsIdsPerInterface = buildSelectedWorkflowConfigurationsGetterObject(
	'userSpecificConfigurationFieldsIdsPerInterface'
);
export const generalConfigurationFieldsIdsPerInterface = buildSelectedWorkflowConfigurationsGetterObject(
	'generalConfigurationFieldsIdsPerInterface'
);
export const configurationsFieldsIdsPerInterface = buildSelectedWorkflowConfigurationsGetterObject('configurationsFieldsIdsPerInterface');

export const userSpecificConfigurationFieldsIds = buildSelectedWorkflowConfigurationsGetterObject('userSpecificConfigurationFieldsIds');
export const generalConfigurationFieldsIds = buildSelectedWorkflowConfigurationsGetterObject('generalConfigurationFieldsIds');
export const configurationsFieldsIds = buildSelectedWorkflowConfigurationsGetterObject('configurationsFieldsIds');

export const doesSelectedWorkflowHasConfigurations = buildSelectedWorkflowConfigurationsGetterObject(
	'doesSelectedWorkflowHasConfigurations'
);
export const userSpecificConfigurationInterface = buildSelectedWorkflowConfigurationsGetterObject('userSpecificConfigurationInterface');
export const generalConfigurationInterface = buildSelectedWorkflowConfigurationsGetterObject('generalConfigurationInterface');
export const getConfigurationAndInterfaceIdByFieldUUID = buildSelectedWorkflowConfigurationsGetterObject(
	'getConfigurationAndInterfaceIdByFieldUUID'
);
export const configurationsMappingBlocksUUIDs = buildSelectedWorkflowConfigurationsGetterObject('configurationsMappingBlocksUUIDs');

const getters = {
	[userSpecificConfigurationFieldsIdsPerInterface.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[getConfigurationFieldsIdsPerInterface.fullPath](getters[userSpecificConfigurationId.getter]),
	[generalConfigurationFieldsIdsPerInterface.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[getConfigurationFieldsIdsPerInterface.fullPath](getters[generalConfigurationId.getter]),
	[configurationsFieldsIdsPerInterface.getter]: (state, getters) => ({
		...getters[generalConfigurationFieldsIdsPerInterface.getter],
		...getters[userSpecificConfigurationFieldsIdsPerInterface.getter],
	}),
	[doesSelectedWorkflowHasConfigurations.getter]: (state, getters) =>
		!!getters[generalConfigurationId.getter] || !!getters[userSpecificConfigurationId.getter],
	[userSpecificConfigurationInterface.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[getWidget.fullPath](rootGetters[getSingleConfigurationInterfaceId.fullPath](getters[userSpecificConfigurationId.getter])),
	[generalConfigurationInterface.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[getWidget.fullPath](rootGetters[getSingleConfigurationInterfaceId.fullPath](getters[generalConfigurationId.getter])),
	[getConfigurationAndInterfaceIdByFieldUUID.getter]: (state, getters, rootState, rootGetters) => (fieldUUID) => {
		const configurations = {
			[getters[userSpecificConfigurationId.getter]]: getters[userSpecificConfiguration.getter],
			[getters[generalConfigurationId.getter]]: getters[generalConfiguration.getter],
		};
		for (const [configurationId, configuration] of Object.entries(configurations)) {
			const workflow_configuration_interfaces = configuration?.workflow_configuration_interfaces || [];

			for (const configuration_interface of workflow_configuration_interfaces) {
				const interfaceId = configuration_interface.interface.id;
				const configurationInterface = rootGetters[getWidget.fullPath](interfaceId);
				const foundField = configurationInterface.fields.find((field) => field.id === fieldUUID);
				if (foundField) {
					return {
						configurationId,
						interfaceUUID: configurationInterface.id,
						interface: configurationInterface,
					};
				}
			}
		}
		return {};
	},
	// Shadows `workflows/getters`
	[userSpecificConfigurationId.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[wfUserSpecificConfigurationId.fullPath](rootGetters[selectedWorkflowId.fullPath]),
	[publishedUserSpecificConfigurationId.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[wfPublishedUserSpecificConfigurationId.fullPath](rootGetters[selectedWorkflowId.fullPath]),
	[userSpecificConfiguration.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[wfUserSpecificConfiguration.fullPath](rootGetters[selectedWorkflowId.fullPath]),
	[generalConfigurationId.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[wfGeneralConfigurationId.fullPath](rootGetters[selectedWorkflowId.fullPath]),
	[publishedGeneralConfigurationId.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[wfPublishedGeneralConfigurationId.fullPath](rootGetters[selectedWorkflowId.fullPath]),
	[generalConfiguration.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[wfGeneralConfiguration.fullPath](rootGetters[selectedWorkflowId.fullPath]),
	[getConfigurationFieldResponse.getter]: (state, getters, rootSate, rootGetters) =>
		rootGetters[wfGetConfigurationFieldResponse.fullPath](rootGetters[selectedWorkflowId.fullPath]),
	[userSpecificConfigurationResponse.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[wfUserSpecificConfigurationResponse.fullPath](rootGetters[selectedWorkflowId.fullPath]),
	[generalConfigurationResponse.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[wfGeneralConfigurationResponse.fullPath](rootGetters[selectedWorkflowId.fullPath]),
	[configurationsResponse.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[wfConfigurationsResponse.fullPath](rootGetters[selectedWorkflowId.fullPath]),

	[userSpecificConfigurationFieldsIds.getter]: (state, getters, rootState, rootGetters) => (fieldType) =>
		rootGetters[getConfigurationFieldsIds.fullPath](getters[userSpecificConfigurationId.getter], fieldType),
	[generalConfigurationFieldsIds.getter]: (state, getters, rootState, rootGetters) => (fieldType) =>
		rootGetters[getConfigurationFieldsIds.fullPath](getters[generalConfigurationId.getter], fieldType),
	[configurationsFieldsIds.getter]: (state, getters) => (fieldType) =>
		[...getters[generalConfigurationFieldsIds.getter](fieldType), ...getters[userSpecificConfigurationFieldsIds.getter](fieldType)],
	[configurationsMappingBlocksUUIDs.getter]: (state, getters) =>
		getters[configurationsFieldsIds.getter](FieldType.UserSchemaMappingsSelector),
};

export default getters;
